import React, { useEffect, useState } from 'react';
import { Users, RefreshCw } from 'lucide-react';
import { doc, updateDoc } from 'firebase/firestore';
import { backofficeDb } from '../backofficeFirebaseClient';
import { backofficeApiFetch } from '../utils/backofficeApi';

const ROLES = ['read_only', 'admin', 'billing_admin', 'super_admin'];

const AdminDirectory = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { admins } = await backofficeApiFetch('/v1/admins');
      setRows(Array.isArray(admins) ? admins : []);
      setLastSyncedAt(new Date().toISOString());
    } catch (e) {
      setError(e.message || 'Failed to load admins');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateRole = async (uid, role) => {
    try {
      await updateDoc(doc(backofficeDb, 'admins', uid), { role, updatedAt: new Date().toISOString() });
      await load();
    } catch (e) {
      setError(e.message || 'Failed to update role');
    }
  };

  const setDisabled = async (uid, disabled) => {
    try {
      await updateDoc(doc(backofficeDb, 'admins', uid), {
        disabled: !!disabled,
        updatedAt: new Date().toISOString()
      });
      await load();
    } catch (e) {
      setError(e.message || 'Failed to update status');
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-gray-700" />
          <h2 className="text-xl font-semibold">Admins</h2>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
      {lastSyncedAt && <p className="text-xs text-gray-500">Last synced: {lastSyncedAt}</p>}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
          {error}
          <p className="text-xs mt-1 text-gray-600">
            Deploy backoffice Cloud Functions (includes GET /v1/admins), then refresh. Role updates still use Firestore rules.
          </p>
        </div>
      )}
      <div className="bg-white border rounded-lg overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-2 border-b">UID</th>
              <th className="p-2 border-b">Email</th>
              <th className="p-2 border-b">Role</th>
              <th className="p-2 border-b">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="p-2 font-mono text-xs">{r.id}</td>
                <td className="p-2">{r.email || '—'}</td>
                <td className="p-2">
                  <select
                    className="border rounded px-2 py-1"
                    value={r.role || 'read_only'}
                    onChange={(e) => updateRole(r.id, e.target.value)}
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="p-2">
                  <button
                    type="button"
                    className="text-xs px-2 py-1 border rounded"
                    onClick={() => setDisabled(r.id, !r.disabled)}
                  >
                    {r.disabled ? 'Enable' : 'Disable'}
                  </button>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && !error && (
              <tr>
                <td colSpan={4} className="p-4 text-gray-500">
                  No admin documents yet. Add <code>admins/&lt;your-uid&gt;</code> in the backoffice Firebase project.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminDirectory;
